const SAFE_PROGRAM_FIELDS = [
    'code',
    'name',
    'description',
    'iconKey',
    'status',
    'access',
];
const SAFE_ACCESS_FIELDS = [
    'companyId',
    'siteId',
    'state',
    'version',
    'accessRequestStatus',
];
const PROGRAM_ACCESS_REQUEST_STATUSES = [
    'PENDING',
    'APPROVED',
    'REJECTED',
    'CANCELLED',
];

// Mirrors ProgramAccessRequestService.ts's escapeHtml() on the server, which
// escapes requesterNote/reviewerNote before returning them in API responses
// (a deliberate, tested control for any non-Vue consumer of this API — see
// Program.access-requests.test.ts). Vue's `{{ }}` interpolation already
// escapes text safely at render time, so this reverses the server escaping
// for display instead of showing literal entities like `&#39;`.
const HTML_ENTITY_DECODE_MAP = {
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&amp;': '&',
};

export function decodeProgramAccessNoteText(value) {
    if (typeof value !== 'string' || !value) return value;
    return value.replace(
        /&lt;|&gt;|&quot;|&#39;|&amp;/g,
        (entity) => HTML_ENTITY_DECODE_MAP[entity]
    );
}

function positiveInteger(value, label) {
    const number = Number(value);
    if (!Number.isInteger(number) || number <= 0) {
        throw new TypeError(`A valid ${label} is required.`);
    }
    return number;
}

export function isProgramEnabled(programOrAccess = {}) {
    const access = programOrAccess.access || programOrAccess;
    return access.state === 'ENABLED';
}

export function isProgramAccessRequestPending(programOrAccess = {}) {
    const access = programOrAccess.access || programOrAccess;
    return access.accessRequestStatus === 'PENDING';
}

export const PROGRAM_ACCESS_STATES = Object.freeze({
    GRANTED: 'access-granted',
    REQUESTABLE: 'access-requestable',
    PENDING: 'access-pending',
    UNAVAILABLE: 'access-unavailable',
});

export function getProgramAccessState(
    program = {},
    { requestPending = false, canRequestAccess = false } = {}
) {
    if (isProgramEnabled(program)) return PROGRAM_ACCESS_STATES.GRANTED;
    if (requestPending || isProgramAccessRequestPending(program)) {
        return PROGRAM_ACCESS_STATES.PENDING;
    }
    return canRequestAccess
        ? PROGRAM_ACCESS_STATES.REQUESTABLE
        : PROGRAM_ACCESS_STATES.UNAVAILABLE;
}

export function isCompanyProgramAdministrator(user = {}, company = {}) {
    return Boolean(
        user.is_company_admin ||
            (user.id && company.admin_id && user.id == company.admin_id)
    );
}

export function canRequestProgramAccess({
    user,
    company,
    program,
    siteId,
} = {}) {
    return Boolean(
        siteId &&
            program?.status === 'PUBLISHED' &&
            !isProgramEnabled(program) &&
            isCompanyProgramAdministrator(user, company)
    );
}

export function buildProgramAccessRequestBody({
    programCode,
    siteId,
    requesterNote,
} = {}) {
    const code = String(programCode || '').trim();
    if (!code) throw new TypeError('A valid Program code is required.');

    const body = {
        programCode: code,
        siteId: positiveInteger(siteId, 'site'),
    };
    const note = String(requesterNote || '').trim();
    if (note) body.requesterNote = note;
    return body;
}

export function programAccessRequestFingerprint(input) {
    return JSON.stringify(buildProgramAccessRequestBody(input));
}

export function createProgramAccessRequestSubmitter(
    client,
    createKey = () => globalThis.crypto.randomUUID()
) {
    let retry = null;
    const inFlight = new Map();

    return {
        submit(input) {
            const body = buildProgramAccessRequestBody(input);
            const fingerprint = JSON.stringify(body);
            if (inFlight.has(fingerprint)) return inFlight.get(fingerprint);

            if (retry?.fingerprint !== fingerprint) {
                retry = { fingerprint, key: createKey() };
            }
            const key = retry.key;
            const request = client
                .request('/program-access-requests', {
                    method: 'POST',
                    body,
                    idempotencyKey: key,
                })
                .then((response) => {
                    if (retry?.fingerprint === fingerprint) retry = null;
                    return response;
                })
                .finally(() => inFlight.delete(fingerprint));
            inFlight.set(fingerprint, request);
            return request;
        },
    };
}

export function canAccessProgramRequestQueue(isAppAdmin) {
    return isAppAdmin === true;
}

export function buildProgramAccessRequestQueueQuery(filters = {}) {
    const status = PROGRAM_ACCESS_REQUEST_STATUSES.includes(filters.status)
        ? filters.status
        : undefined;
    const companyId = Number(filters.companyId);
    const siteId = Number(filters.siteId);
    const programCode = String(filters.programCode || '').trim();

    return {
        ...(status ? { status } : {}),
        ...(Number.isInteger(companyId) && companyId > 0 ? { companyId } : {}),
        ...(Number.isInteger(siteId) && siteId > 0 ? { siteId } : {}),
        ...(programCode ? { programCode } : {}),
        page: Math.max(1, Number(filters.page) || 1),
        pageSize: Math.min(100, Math.max(1, Number(filters.pageSize) || 10)),
    };
}

export async function fetchPendingProgramAccessRequestCount(client) {
    const response = await client.request('/program-access-requests', {
        query: buildProgramAccessRequestQueueQuery({
            status: 'PENDING',
            pageSize: 1,
        }),
    });
    return Number(response?.pagination?.totalItems) || 0;
}

export function programAccessRequestPath(requestId) {
    return `/program-access-requests/${positiveInteger(requestId, 'request')}`;
}

export function buildProgramAccessReviewBody({ status, reviewerNote } = {}) {
    if (!['APPROVED', 'REJECTED'].includes(status)) {
        throw new TypeError('A valid review decision is required.');
    }
    const body = { status };
    const note = String(reviewerNote || '').trim();
    if (note) body.reviewerNote = note;
    return body;
}

export function createProgramAccessRequestReviewer(
    client,
    createKey = () => globalThis.crypto.randomUUID()
) {
    const retryKeys = new Map();
    const inFlight = new Map();

    return {
        review(request, input) {
            const requestId = positiveInteger(request?.id, 'request');
            const version = positiveInteger(
                request?.version,
                'request version'
            );
            const body = buildProgramAccessReviewBody(input);
            const fingerprint = JSON.stringify({ requestId, version, body });
            if (inFlight.has(fingerprint)) return inFlight.get(fingerprint);

            const idempotencyKey =
                retryKeys.get(fingerprint) || createKey(fingerprint);
            retryKeys.set(fingerprint, idempotencyKey);
            const pending = client
                .request(programAccessRequestPath(requestId), {
                    method: 'PATCH',
                    body,
                    ifMatch: version,
                    idempotencyKey,
                })
                .then((response) => {
                    retryKeys.delete(fingerprint);
                    return response;
                })
                .finally(() => inFlight.delete(fingerprint));
            inFlight.set(fingerprint, pending);
            return pending;
        },
    };
}

export function applyProgramAccessReview(items, reviewed, statusFilter = '') {
    const rows = Array.isArray(items) ? items : [];
    if (!reviewed || !rows.some(({ id }) => id === reviewed.id)) return rows;
    if (statusFilter && reviewed.status !== statusFilter) {
        return rows.filter(({ id }) => id !== reviewed.id);
    }
    return rows.map((row) => {
        if (row.id !== reviewed.id) return row;
        return row.status === reviewed.status &&
            row.version === reviewed.version
            ? row
            : reviewed;
    });
}

export function sortProgramAccessRequestHistory(entries) {
    return [...entries]
        .sort((left, right) => {
            const leftTime = new Date(left.timestamp).getTime();
            const rightTime = new Date(right.timestamp).getTime();
            return (
                (Number.isNaN(rightTime) ? 0 : rightTime) -
                (Number.isNaN(leftTime) ? 0 : leftTime)
            );
        })
        .map((entry, index) => ({ ...entry, latest: index === 0 }));
}

export function isProgramAlreadyEnabledError(error) {
    return error?.code === 'PROGRAM_ALREADY_ENABLED_AT_SITE';
}

export function programAccessRequestError(error) {
    if (error?.status === 400) {
        return 'Check the request and try again.';
    }
    if (error?.status === 401) {
        return 'Your session has expired. Sign in and try again.';
    }
    if (error?.status === 403) {
        return 'You do not have permission to request access.';
    }
    if (error?.status === 404) {
        return 'This Program is no longer available.';
    }
    if (error?.status === 409) {
        return 'This request conflicts with a newer request. Try again.';
    }
    if (error?.status === 422) {
        return 'The access request could not be submitted.';
    }
    if (error?.status === 429) {
        return 'Too many requests. Wait a moment and try again.';
    }
    return 'Access could not be requested. Try again.';
}

export function safeDisabledProgram(program = {}) {
    const safeProgram = Object.fromEntries(
        SAFE_PROGRAM_FIELDS.filter((field) => program[field] !== undefined).map(
            (field) => [field, program[field]]
        )
    );
    if (safeProgram.access) {
        safeProgram.access = Object.fromEntries(
            SAFE_ACCESS_FIELDS.filter(
                (field) => safeProgram.access[field] !== undefined
            ).map((field) => [field, safeProgram.access[field]])
        );
    }
    return safeProgram;
}

export function programAccessUnavailableReason() {
    return 'This Program is not enabled at the selected site.';
}

export function companySiteProgramEntitlementPath(
    companyId,
    siteId,
    programCode
) {
    if (!String(programCode || '').trim()) {
        throw new TypeError('A valid Program code is required.');
    }
    return `/companies/${positiveInteger(
        companyId,
        'company'
    )}/sites/${positiveInteger(
        siteId,
        'site'
    )}/program-entitlements/${encodeURIComponent(programCode)}`;
}

export function canManageProgramAccess(isAppAdmin) {
    return isAppAdmin === true;
}

export function programAccessMutationError(error) {
    if (error?.status === 409 || error?.code === 'VERSION_CONFLICT') {
        return 'Program access changed. The latest access state has been loaded.';
    }
    return error?.message || 'Could not update Program access.';
}
